/**
 * Spec 094: lightweight Danish-vs-English language detection for AIO snippet
 * cards. Pure function, no LLM, no new dependency.
 *
 * Algorithm (per clarifications.md Q4):
 *   1. Tokenize on Unicode word boundaries.
 *   2. Lowercase, count matches against two ~30-word stopword sets.
 *   3. Return 'da' if daHits >= enHits + 2 AND daHits >= 3
 *           'en' if enHits >= daHits + 2 AND enHits >= 3
 *           'unknown' otherwise (including text < 30 chars).
 *
 * The output is used by AioResponseBody to render an `EN` / `DA` pill on
 * snippet cards whose detected language differs from the brand's primary
 * locale. The pill is a soft hint; false positives are acceptable because
 * the badge is non-load-bearing (consultant can always read the snippet).
 */

const DA_STOPWORDS = new Set<string>([
  'og', 'er', 'det', 'en', 'et', 'at', 'til', 'for', 'af', 'på',
  'med', 'som', 'har', 'jeg', 'du', 'vi', 'ikke', 'men', 'kan', 'vil',
  'der', 'den', 'de', 'dette', 'disse', 'fra', 'om', 'ved', 'eller', 'så',
  'hvis',
]);

const EN_STOPWORDS = new Set<string>([
  'the', 'of', 'and', 'to', 'a', 'in', 'is', 'it', 'you', 'that',
  'was', 'for', 'on', 'are', 'with', 'as', 'this', 'an', 'be', 'at',
  'by', 'not', 'have', 'from', 'or', 'but', 'can', 'has', 'had', 'were',
]);

export type DetectedLang = 'da' | 'en' | 'unknown';

export function detectSnippetLanguage(text: string): DetectedLang {
  if (!text || text.length < 30) return 'unknown';

  // Tokenize on Unicode word boundaries. \p{L} = any letter (Unicode-aware).
  // Falls back gracefully if the engine doesn't support \p escapes (modern
  // browsers and Node 18+ do).
  let tokens: string[];
  try {
    tokens = text.toLowerCase().match(/\p{L}+/gu) ?? [];
  } catch {
    tokens = text.toLowerCase().match(/[a-zæøåäöüß]+/g) ?? [];
  }

  if (tokens.length === 0) return 'unknown';

  let daHits = 0;
  let enHits = 0;
  for (const tok of tokens) {
    if (DA_STOPWORDS.has(tok)) daHits++;
    if (EN_STOPWORDS.has(tok)) enHits++;
  }

  if (daHits >= 3 && daHits >= enHits + 2) return 'da';
  if (enHits >= 3 && enHits >= daHits + 2) return 'en';
  return 'unknown';
}

/**
 * Extract the language portion of a BCP-47 locale tag.
 * `'da-DK'` → `'da'`, `'en-US'` → `'en'`, `null` → `null`.
 */
export function bcp47Language(locale: string | null | undefined): string | null {
  if (!locale) return null;
  const lower = locale.toLowerCase();
  const dash = lower.indexOf('-');
  return dash > 0 ? lower.slice(0, dash) : lower;
}
